import { AdminHeader } from "@/components/AdminHeader";
import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { ExternalLink, FileImage, CheckCircle2 } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const ROW = "border-[#E5DED5] hover:bg-[#F5EFE6]/50 transition-colors";

export default function LicensesTab() {
  const [licenses, setLicenses] = useState(null);
  const [photos, setPhotos] = useState([]);

  const load = () => {
    api.get("/admin/photo-records").then((r) => { setLicenses(r.data.licenses); setPhotos(r.data.vehicle_photos); }).catch(() => setLicenses([]));
  };
  useEffect(() => { load(); }, []);

  const approvePhoto = async (p) => {
    try {
      await api.patch(`/admin/vehicle-photos/${p.id}/approve`);
      toast.success("Damage photo approved");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const editNote = async (p) => {
    const note = window.prompt("Note for this photo (damage details, location…):", p.note || "");
    if (note === null) return;
    try {
      await api.patch(`/admin/vehicle-photos/${p.id}`, { note });
      toast.success("Note saved");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const approve = async (l) => {
    try {
      await api.patch(`/admin/licenses/${l.id}/approve`);
      toast.success(`Approved — ${l.customer_name || "customer"} will be emailed their Verified Renter badge once all documents are approved`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (licenses === null) return <p className="text-muted-foreground font-ui">Loading licenses…</p>;

  return (
    <div data-testid="admin-licenses-tab">
      <AdminHeader testid="admin-header-licenses" eyebrow="Documents" title="Licenses & Returns" description="Review customer documents and return photos. Approve each document — when all of a booking's documents are approved, the renter gets their Verified Renter email." />
      <p className="mb-4 font-ui text-xs tracking-[0.2em] uppercase text-[#4A4A4A]">Uploaded Documents</p>
      <div className="bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-2xl overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="border-[#E5DED5] hover:bg-transparent">
            <TableHead>Booking Ref</TableHead>
            <TableHead>Doc Type</TableHead>
            <TableHead>Customer</TableHead>
            <TableHead>File</TableHead>
            <TableHead>Uploaded</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {licenses.length === 0 && (
            <TableRow className={ROW}>
              <TableCell colSpan={7} data-testid="licenses-empty" className="text-muted-foreground font-ui py-10 text-center">
                No driver's licenses uploaded yet.
              </TableCell>
            </TableRow>
          )}
          {licenses.map((l) => (
            <TableRow key={l.id} data-testid={`license-row-${l.id}`} className={ROW}>
              <TableCell className="text-[#C71E2E] font-ui font-medium whitespace-nowrap">{l.booking_ref || "—"}</TableCell>
              <TableCell className="text-muted-foreground capitalize whitespace-nowrap">{(l.doc_type || "license_front").replace(/_/g, " ")}</TableCell>
              <TableCell>
                <p className="font-ui">{l.customer_name || "—"}</p>
                <p className="text-xs text-muted-foreground">{l.customer_email}</p>
              </TableCell>
              <TableCell className="text-muted-foreground">
                <span className="flex items-center gap-2">
                  <FileImage size={14} className="text-[#C71E2E] shrink-0" />
                  <span className="truncate max-w-[180px]">{l.original_filename}</span>
                </span>
              </TableCell>
              <TableCell className="text-muted-foreground whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</TableCell>
              <TableCell>
                <span data-testid={`license-status-${l.id}`} className={`text-[10px] font-ui tracking-[0.15em] uppercase border rounded-full px-3 py-1 whitespace-nowrap ${l.approved ? "text-emerald-700 border-emerald-200 bg-emerald-50" : "text-amber-700 border-amber-200 bg-amber-50"}`}>
                  {l.approved ? "Approved" : "Pending"}
                </span>
              </TableCell>
              <TableCell className="text-right whitespace-nowrap">
                <a
                  data-testid={`license-view-${l.id}`}
                  href={`${API_BASE}/api/licenses/${l.id}/file`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm font-ui text-muted-foreground hover:text-[#C71E2E] transition-colors duration-200 mr-3"
                >
                  View <ExternalLink size={13} />
                </a>
                {!l.approved && (
                  <Button data-testid={`license-approve-${l.id}`} size="sm" variant="outline" onClick={() => approve(l)} className="rounded-full border-emerald-200 bg-transparent text-emerald-700 hover:bg-emerald-50 font-ui">
                    <CheckCircle2 size={13} className="mr-1.5" /> Approve
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      </div>

      <p className="mt-10 mb-4 font-ui text-xs tracking-[0.2em] uppercase text-[#4A4A4A]">Damage / Pickup Inspection Photos (start of rental)</p>
      <div data-testid="admin-pickup-photos-table" className="bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-2xl overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-[#E5DED5] hover:bg-transparent">
              <TableHead>Booking Ref</TableHead>
              <TableHead>Side</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Uploaded</TableHead>
              <TableHead>Note</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {photos.filter((p) => p.phase === "pickup").length === 0 && (
              <TableRow className={ROW}><TableCell colSpan={7} data-testid="pickup-photos-empty" className="text-muted-foreground font-ui py-10 text-center">No pickup inspection photos yet.</TableCell></TableRow>
            )}
            {photos.filter((p) => p.phase === "pickup").map((p) => (
              <TableRow key={p.id} data-testid={`pickup-photo-row-${p.id}`} className={ROW}>
                <TableCell className="text-[#C71E2E] font-ui font-medium whitespace-nowrap">{p.booking_ref || "—"}</TableCell>
                <TableCell className="text-muted-foreground capitalize whitespace-nowrap">{p.photo_type}</TableCell>
                <TableCell><p className="font-ui text-sm">{p.customer_email}</p></TableCell>
                <TableCell className="text-muted-foreground whitespace-nowrap text-sm">{p.uploaded_at ? new Date(p.uploaded_at).toLocaleString() : "—"}</TableCell>
                <TableCell className="text-muted-foreground text-xs max-w-[160px] truncate">{p.note || "—"}</TableCell>
                <TableCell>
                  <span data-testid={`pickup-photo-status-${p.id}`} className={`text-[10px] font-ui tracking-[0.15em] uppercase border rounded-full px-3 py-1 whitespace-nowrap ${p.approved ? "text-emerald-700 border-emerald-200 bg-emerald-50" : "text-amber-700 border-amber-200 bg-amber-50"}`}>
                    {p.approved ? "Approved" : "Pending"}
                  </span>
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <a data-testid={`pickup-photo-view-${p.id}`} href={`${API_BASE}/api/vehicle-photos/${p.id}/file`} target="_blank" rel="noopener noreferrer" className="text-sm font-ui text-muted-foreground hover:text-[#C71E2E] transition-colors duration-200 mr-3">View</a>
                  <a data-testid={`pickup-photo-download-${p.id}`} href={`${API_BASE}/api/vehicle-photos/${p.id}/file?download=1`} className="text-sm font-ui text-muted-foreground hover:text-[#C71E2E] transition-colors duration-200 mr-3">Download</a>
                  <Button size="sm" variant="outline" data-testid={`pickup-photo-note-${p.id}`} onClick={() => editNote(p)} className="rounded-full border-[#E5DED5] bg-transparent hover:bg-[#F5EFE6] font-ui mr-2">Note</Button>
                  {!p.approved && (
                    <Button size="sm" variant="outline" data-testid={`pickup-photo-approve-${p.id}`} onClick={() => approvePhoto(p)} className="rounded-full border-emerald-200 bg-transparent text-emerald-700 hover:bg-emerald-50 font-ui">
                      <CheckCircle2 size={13} className="mr-1.5" /> Approve
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <p className="mt-10 mb-4 font-ui text-xs tracking-[0.2em] uppercase text-[#4A4A4A]">Return Photos (end of rental)</p>
      <div data-testid="admin-returns-table" className="bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-2xl overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-[#E5DED5] hover:bg-transparent">
              <TableHead>Booking Ref</TableHead>
              <TableHead>Photo</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Uploaded</TableHead>
              <TableHead>GPS</TableHead>
              <TableHead>Note</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {photos.filter((p) => p.phase === "return").length === 0 && (
              <TableRow className={ROW}><TableCell colSpan={7} data-testid="returns-empty" className="text-muted-foreground font-ui py-10 text-center">No return photos yet.</TableCell></TableRow>
            )}
            {photos.filter((p) => p.phase === "return").map((p) => (
              <TableRow key={p.id} data-testid={`return-photo-row-${p.id}`} className={ROW}>
                <TableCell className="text-[#C71E2E] font-ui font-medium whitespace-nowrap">{p.booking_ref || "—"}</TableCell>
                <TableCell className="text-muted-foreground capitalize whitespace-nowrap">{p.photo_type}</TableCell>
                <TableCell><p className="font-ui text-sm">{p.customer_email}</p></TableCell>
                <TableCell className="text-muted-foreground whitespace-nowrap text-sm">{p.uploaded_at ? new Date(p.uploaded_at).toLocaleString() : "—"}</TableCell>
                <TableCell className="whitespace-nowrap">
                  {p.gps?.lat ? (
                    <>
                      <a data-testid={`return-photo-gps-${p.id}`} href={`https://www.google.com/maps?q=${p.gps.lat},${p.gps.lng}`} target="_blank" rel="noopener noreferrer" className="text-sm font-ui text-[#C71E2E] underline underline-offset-2">Map ✓</a>
                      {p.gps?.geofence === "alert" && (
                        <span data-testid={`return-photo-geofence-${p.id}`} className="ml-1.5 text-[10px] font-ui font-semibold uppercase tracking-wide text-red-700 border border-red-300 bg-red-50 rounded-full px-2 py-0.5 whitespace-nowrap">Outside zone</span>
                      )}
                    </>
                  ) : <span className="text-muted-foreground text-xs">—</span>}
                </TableCell>
                <TableCell className="text-muted-foreground text-xs max-w-[160px] truncate">{p.note || "—"}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <a data-testid={`return-photo-view-${p.id}`} href={`${API_BASE}/api/vehicle-photos/${p.id}/file`} target="_blank" rel="noopener noreferrer" className="text-sm font-ui text-muted-foreground hover:text-[#C71E2E] transition-colors duration-200 mr-3">View</a>
                  <a data-testid={`return-photo-download-${p.id}`} href={`${API_BASE}/api/vehicle-photos/${p.id}/file?download=1`} className="text-sm font-ui text-muted-foreground hover:text-[#C71E2E] transition-colors duration-200 mr-3">Download</a>
                  <Button size="sm" variant="outline" data-testid={`return-photo-note-${p.id}`} onClick={() => editNote(p)} className="rounded-full border-[#E5DED5] bg-transparent hover:bg-[#F5EFE6] font-ui">Note</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
